import { useCallback, useEffect, useState, type ChangeEvent } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { saveRegisterFile, type Base64File } from 'shared/components/partner-registers/register-file'
import { Download } from 'lucide-react'
import { CompactTable, EmptyTableBody, SectionTitle } from 'shared/components/section'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Button } from 'shared/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { useTranslate } from 'shared/lib/i18n'
import { readAsBase64 } from 'shared/lib/utils'
import { formatDayTime } from 'shared/lib/utils/date'

/**
 * «Инвентаризация» of a Point on TezGo, on «Локации» (ADR-0045, Release 2 ticket 54):
 * one partner's count of the Point's shelf in a day.
 *
 *  - **Открыть** — one per partner whose shelf list TezGo takes: TezTez's from `back`,
 *    139 Express's by its shelf file.
 *  - **Забрать полку из back** / **Забрать ещё раз** (TezTez) — TezGo reads `back`'s
 *    export of the shelf and keeps it as rows; taking it again adds only new rows.
 *  - **Загрузить файл полки** (139) — preview → confirm (ADR-0023); a row of another
 *    Point or without an amount is refused alone, in words.
 *  - **Отсканировано X из N**, the rows not found yet, the extras.
 *  - **Закрыть инвентаризацию** — with a confirmation: a row not scanned becomes «не
 *    найдена», with no Parcel and no fact.
 *  - **Скачать итог** — Excel: taken, not found, extras.
 *
 * The section loads only when the admin opens it — a list of a hundred Points does not
 * ask the server a hundred times. Every count, row and refusal is the server's; the
 * calls are injected so the screen is tested without a backend.
 */
type Location = RouterOutputs['locations']['List'][number]
export type InventoryOfPoint = RouterOutputs['inventories']['OfPoint']
export type InventoryDetail = RouterOutputs['inventories']['Detail']
export type InventoryOpenAnswer = RouterOutputs['inventories']['Open']
export type InventoryTakeAnswer = RouterOutputs['inventories']['TakeFromBack']
export type InventoryPreviewAnswer = RouterOutputs['inventories']['FilePreview']
export type InventoryConfirmAnswer = RouterOutputs['inventories']['FileConfirm']
export type InventoryCloseAnswer = RouterOutputs['inventories']['Close']
type Summary = InventoryOfPoint['inventories'][number]
type Plan = Extract<InventoryPreviewAnswer, { outcome: 'planned' }>

export interface PointInventoryCalls {
	ofPoint: (input: RouterInputs['inventories']['OfPoint']) => Promise<InventoryOfPoint>
	detail: (input: RouterInputs['inventories']['Detail']) => Promise<InventoryDetail>
	open: (input: RouterInputs['inventories']['Open']) => Promise<InventoryOpenAnswer>
	takeFromBack: (input: RouterInputs['inventories']['TakeFromBack']) => Promise<InventoryTakeAnswer>
	filePreview: (input: RouterInputs['inventories']['FilePreview']) => Promise<InventoryPreviewAnswer>
	fileConfirm: (input: RouterInputs['inventories']['FileConfirm']) => Promise<InventoryConfirmAnswer>
	close: (input: RouterInputs['inventories']['Close']) => Promise<InventoryCloseAnswer>
	result: (input: RouterInputs['inventories']['Result']) => Promise<Base64File>
	saveFile: (file: Base64File) => void
	readFile: (file: File) => Promise<string>
}

const viaTrpc: PointInventoryCalls = {
	ofPoint: (input) => trpcClient.inventories.OfPoint.query(input),
	detail: (input) => trpcClient.inventories.Detail.query(input),
	open: (input) => trpcClient.inventories.Open.mutate(input),
	takeFromBack: (input) => trpcClient.inventories.TakeFromBack.mutate(input),
	filePreview: (input) => trpcClient.inventories.FilePreview.mutate(input),
	fileConfirm: (input) => trpcClient.inventories.FileConfirm.mutate(input),
	close: (input) => trpcClient.inventories.Close.mutate(input),
	result: (input) => trpcClient.inventories.Result.query(input),
	saveFile: saveRegisterFile,
	readFile: readAsBase64,
}

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

type Message = { ok: boolean; text: string }

export function PointInventory({ location, calls = viaTrpc }: { location: Location; calls?: PointInventoryCalls }) {
	const { t } = useTranslate()
	const [shown, setShown] = useState(false)
	const [state, setState] = useState<InventoryOfPoint | null>(null)
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<Message | null>(null)

	const reload = useCallback(async () => {
		setState(await calls.ofPoint({ pointId: location._id }))
	}, [calls, location._id])

	useEffect(() => {
		if (!shown) return
		reload().catch((error) => setMessage({ ok: false, text: t('inventory.failed', { message: failure(error) }) }))
	}, [shown, reload, t])

	const run = async (act: () => Promise<Message | null>) => {
		if (busy) return
		setBusy(true)
		setMessage(null)
		try {
			const result = await act()
			if (result) setMessage(result)
		} catch (error) {
			setMessage({ ok: false, text: t('inventory.failed', { message: failure(error) }) })
		} finally {
			setBusy(false)
		}
	}

	const onOpen = (partnerId: string) =>
		run(async () => {
			const answer = await calls.open({ pointId: location._id, partnerId })
			await reload()
			return answer.outcome === 'refused'
				? { ok: false, text: t(`inventoryRefusal.${answer.reason}`) }
				: { ok: true, text: t('inventory.opened') }
		})

	const onDownload = (inventoryId: string) =>
		run(async () => {
			calls.saveFile(await calls.result({ inventoryId }))
			return null
		})

	const closedOnes = state?.inventories.filter((i) => i.state === 'CLOSED') ?? []

	return (
		<section className='flex flex-col gap-3' data-testid='point-inventory'>
			<SectionTitle
				title={t('inventory.title')}
				hint={t('inventory.hint')}
				aside={
					<Button size='sm' variant='outline' onClick={() => setShown((s) => !s)} data-testid='point-inventory-toggle'>
						{shown ? t('inventory.hide') : t('inventory.show')}
					</Button>
				}
			/>
			{shown && (
				<>
					{!state ? (
						<p className='text-sm text-muted-foreground'>{t('inventory.loading')}</p>
					) : state.partners.length === 0 ? (
						<p className='text-sm text-muted-foreground' data-testid='point-inventory-no-partners'>
							{t('inventory.noPartners')}
						</p>
					) : (
						state.partners.map((partner) => (
							<div
								key={partner.partnerId}
								className='flex flex-col gap-3 border-t pt-3'
								data-testid={`point-inventory-partner-${partner.source}`}
							>
								<div className='flex flex-wrap items-center gap-2'>
									<span className='text-sm font-medium'>{partner.name}</span>
									<span className='text-sm text-muted-foreground'>{t(`inventory.source_${partner.source}`)}</span>
									{!partner.openInventoryId && (
										<Button
											size='sm'
											variant='outline'
											className='ml-auto'
											disabled={busy}
											onClick={() => onOpen(partner.partnerId)}
											data-testid='point-inventory-open'
										>
											{t('inventory.open')}
										</Button>
									)}
								</div>
								{partner.openInventoryId && (
									<OpenInventory
										key={partner.openInventoryId}
										inventoryId={partner.openInventoryId}
										source={partner.source}
										calls={calls}
										onClosed={async () => {
											await reload()
											setMessage({ ok: true, text: t('inventory.closed') })
										}}
										onDownload={onDownload}
									/>
								)}
							</div>
						))
					)}
					{closedOnes.length > 0 && (
						<div className='mt-2 flex flex-col gap-2' data-testid='point-inventory-history'>
							<span className='text-sm font-medium'>{t('inventory.history')}</span>
							<CompactTable>
								<TableHeader>
									<TableRow>
										<TableHead>{t('inventory.colPartner')}</TableHead>
										<TableHead>{t('inventory.colPeriod')}</TableHead>
										<TableHead className='text-right'>{t('inventory.colTaken')}</TableHead>
										<TableHead className='text-right'>{t('inventory.colNotFound')}</TableHead>
										<TableHead className='text-right'>{t('inventory.colExtras')}</TableHead>
										<TableHead />
									</TableRow>
								</TableHeader>
								<TableBody>
									{closedOnes.map((i) => (
										<ClosedLine key={i.inventoryId} summary={i} busy={busy} onDownload={onDownload} />
									))}
								</TableBody>
							</CompactTable>
						</div>
					)}
					{message && (
						<p
							className={message.ok ? 'text-sm text-emerald-700 dark:text-emerald-400' : 'text-sm text-destructive'}
							data-testid='point-inventory-message'
						>
							{message.text}
						</p>
					)}
				</>
			)}
		</section>
	)
}

function ClosedLine({
	summary,
	busy,
	onDownload,
}: {
	summary: Summary
	busy: boolean
	onDownload: (inventoryId: string) => void
}) {
	const { t } = useTranslate()
	return (
		<TableRow data-testid='point-inventory-closed'>
			<TableCell>{summary.partnerName}</TableCell>
			<TableCell className='text-muted-foreground'>
				{formatDayTime(summary.openedAt)} — {summary.closedAt ? formatDayTime(summary.closedAt) : '—'}
			</TableCell>
			<TableCell className='text-right tabular-nums'>{summary.counts.taken}</TableCell>
			<TableCell className='text-right tabular-nums'>{summary.counts.notFound}</TableCell>
			<TableCell className='text-right tabular-nums'>{summary.counts.extras}</TableCell>
			<TableCell className='w-px text-right'>
				<Button
					size='icon-sm'
					variant='ghost'
					disabled={busy}
					aria-label={t('inventory.download')}
					title={t('inventory.download')}
					onClick={() => onDownload(summary.inventoryId)}
				>
					<Download />
				</Button>
			</TableCell>
		</TableRow>
	)
}

/** One open inventory: its progress, its list, the list's taking, its close and its result. */
function OpenInventory({
	inventoryId,
	source,
	calls,
	onClosed,
	onDownload,
}: {
	inventoryId: string
	source: 'back' | 'file'
	calls: PointInventoryCalls
	/** The inventory is closed: the Point's section reads itself again and says so. */
	onClosed: () => Promise<void>
	onDownload: (inventoryId: string) => void
}) {
	const { t } = useTranslate()
	const [detail, setDetail] = useState<InventoryDetail | null>(null)
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<Message | null>(null)
	const [file, setFile] = useState<{ base64: string; name: string } | null>(null)
	const [plan, setPlan] = useState<Plan | null>(null)
	const [confirmingClose, setConfirmingClose] = useState(false)

	const load = useCallback(async () => {
		setDetail(await calls.detail({ inventoryId }))
	}, [calls, inventoryId])

	useEffect(() => {
		load().catch((error) => setMessage({ ok: false, text: t('inventory.failed', { message: failure(error) }) }))
	}, [load, t])

	const run = async (act: () => Promise<Message | null>) => {
		if (busy) return
		setBusy(true)
		setMessage(null)
		try {
			const result = await act()
			if (result) setMessage(result)
		} catch (error) {
			setMessage({ ok: false, text: t('inventory.failed', { message: failure(error) }) })
		} finally {
			setBusy(false)
		}
	}

	const onTake = () =>
		run(async () => {
			const answer = await calls.takeFromBack({ inventoryId })
			if (answer.outcome === 'refused') return { ok: false, text: t(`inventoryRefusal.${answer.reason}`) }
			await load()
			return {
				ok: true,
				text: t('inventory.taken', {
					listed: String(answer.listed),
					added: String(answer.added),
					updated: String(answer.updated),
					already: String(answer.alreadyOnList),
					led: String(answer.ledByTezgo),
					foreign: String(answer.foreign),
					withoutAmount: String(answer.withoutAmount),
				}),
			}
		})

	const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
		const chosen = event.target.files?.[0]
		setPlan(null)
		setMessage(null)
		if (!chosen) {
			setFile(null)
			return
		}
		setFile({ base64: await calls.readFile(chosen), name: chosen.name })
	}

	const fileRefusal = (answer: Extract<InventoryPreviewAnswer, { outcome: 'refused' }>): Message => ({
		ok: false,
		text:
			answer.reason === 'shelf_file_missing_columns'
				? t('inventory.missingColumns', { columns: answer.missingColumns.join(', ') })
				: t(`inventoryRefusal.${answer.reason}`),
	})

	const onPreview = () =>
		run(async () => {
			if (!file) return null
			const answer = await calls.filePreview({ inventoryId, fileBase64: file.base64, fileName: file.name })
			if (answer.outcome === 'refused') return fileRefusal(answer)
			setPlan(answer)
			return null
		})

	const onConfirm = () =>
		run(async () => {
			if (!file || !plan) return null
			const answer = await calls.fileConfirm({
				inventoryId,
				fileBase64: file.base64,
				fileName: file.name,
				planHash: plan.planHash,
			})
			if (answer.outcome === 'refused') {
				setPlan(null)
				return fileRefusal(answer)
			}
			setPlan(null)
			setFile(null)
			await load()
			return {
				ok: true,
				text: t('inventory.added', { count: String(answer.added), updated: String(answer.updated) }),
			}
		})

	const onClose = () =>
		run(async () => {
			await calls.close({ inventoryId })
			setConfirmingClose(false)
			await onClosed()
			return null
		})

	if (!detail) {
		return (
			<p className='text-sm text-muted-foreground'>
				{message ? message.text : t('inventory.loading')}
			</p>
		)
	}
	const listed = detail.counts.total > 0
	const messageLine = message && (
		<p
			className={message.ok ? 'text-sm text-emerald-700 dark:text-emerald-400' : 'text-sm text-destructive'}
			data-testid='point-inventory-open-message'
		>
			{message.text}
		</p>
	)

	return (
		<div className='flex flex-col gap-3' data-testid='point-inventory-open-one'>
			<div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
				<span className='text-lg font-semibold tabular-nums' data-testid='point-inventory-progress'>
					{t('inventory.progress', { taken: String(detail.counts.taken), total: String(detail.counts.total) })}
					{detail.counts.extras > 0 && (
						<span className='ml-2 text-sm font-normal text-amber-700 dark:text-amber-400'>
							{t('inventory.extrasCount', { count: String(detail.counts.extras) })}
						</span>
					)}
				</span>
				<span className='text-sm text-muted-foreground'>
					{t('inventory.stateOpen', { date: formatDayTime(detail.openedAt), name: detail.openedBy || '—' })}
				</span>
			</div>

			<div className='flex flex-wrap items-center gap-2'>
				{source === 'back' && (
					<Button size='sm' disabled={busy} onClick={onTake} data-testid='point-inventory-take'>
						{listed ? t('inventory.takeAgain') : t('inventory.takeFromBack')}
					</Button>
				)}
				<Button
					size='sm'
					variant='outline'
					disabled={busy}
					onClick={() => onDownload(inventoryId)}
					data-testid='point-inventory-download'
				>
					{t('inventory.download')}
				</Button>
				<Button
					size='sm'
					variant='destructive'
					className='ml-auto'
					disabled={busy}
					onClick={() => setConfirmingClose(true)}
					data-testid='point-inventory-close'
				>
					{t('inventory.close')}
				</Button>
			</div>

			{source === 'file' && (
				<div className='flex flex-col gap-2'>
					<div className='flex flex-wrap items-center gap-2'>
						<Label htmlFor={`shelf-file-${inventoryId}`} className='font-normal text-muted-foreground'>
							{listed ? t('inventory.uploadAgain') : t('inventory.fileLabel')}
						</Label>
						<Input
							id={`shelf-file-${inventoryId}`}
							type='file'
							accept='.xlsx'
							className='h-8 w-64'
							onChange={onFile}
							data-testid='point-inventory-file'
						/>
						<Button
							size='sm'
							variant='outline'
							disabled={busy || !file}
							onClick={onPreview}
							data-testid='point-inventory-preview'
						>
							{t('inventory.preview')}
						</Button>
					</div>
					{plan && (
						<div className='flex flex-col gap-2 rounded-md border p-3 text-sm' data-testid='point-inventory-plan'>
							<span data-testid='point-inventory-plan-summary'>
								{t('inventory.planSummary', {
									rows: String(plan.rows),
									toAdd: String(plan.toAdd),
									toUpdate: String(plan.toUpdate),
									already: String(plan.alreadyOnList),
									led: String(plan.ledByTezgo),
									refused: String(plan.refused.length),
								})}
							</span>
							{plan.refused.map((r) => (
								<span key={r.row} className='text-destructive' data-testid='point-inventory-plan-refused'>
									{t('inventory.refusedRow', {
										row: String(r.row),
										code: r.trackingCode || '—',
										reason: t(`inventoryRowRefusal.${r.reason}`),
									})}
								</span>
							))}
							<Button
								size='sm'
								className='w-fit'
								disabled={busy || plan.toAdd === 0}
								onClick={onConfirm}
								data-testid='point-inventory-confirm'
							>
								{t('inventory.confirm')}
							</Button>
						</div>
					)}
				</div>
			)}

			{messageLine}

			<RowList title={t('inventory.missingTitle')} testId='point-inventory-missing' rows={detail.missing} />
			{detail.extras.length > 0 && (
				<div className='flex flex-col gap-1.5' data-testid='point-inventory-extras'>
					<span className='text-sm font-medium'>{t('inventory.extrasTitle')}</span>
					<CompactTable>
						<TableHeader>
							<TableRow>
								<TableHead>{t('inventory.colCode')}</TableHead>
								<TableHead>{t('inventory.colScanned')}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{detail.extras.map((e) => (
								<TableRow key={e.code}>
									<TableCell className='font-mono'>{e.code}</TableCell>
									<TableCell className='text-muted-foreground'>{formatDayTime(e.at)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</CompactTable>
				</div>
			)}

			<AlertDialog open={confirmingClose} onOpenChange={(open) => !open && !busy && setConfirmingClose(false)}>
				<AlertDialogContent data-testid='point-inventory-close-confirm'>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('inventory.closeAsk')}</AlertDialogTitle>
						<AlertDialogDescription>{t('inventory.closeConfirm')}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={busy}>{t('inventory.closeNo')}</AlertDialogCancel>
						<Button variant='destructive' disabled={busy} onClick={onClose} data-testid='point-inventory-close-yes'>
							{t('inventory.closeYes')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}

function RowList({ title, testId, rows }: { title: string; testId: string; rows: InventoryDetail['missing'] }) {
	const { t } = useTranslate()
	return (
		<div className='flex flex-col gap-1.5' data-testid={testId}>
			<SectionTitle title={title} count={rows.length} />
			<div className='max-h-72 overflow-y-auto rounded-md border'>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{t('inventory.colCode')}</TableHead>
							<TableHead>{t('inventory.colRecipient')}</TableHead>
							<TableHead className='text-right'>{t('inventory.colAmount')}</TableHead>
							<TableHead />
						</TableRow>
					</TableHeader>
					{rows.length === 0 ? (
						<EmptyTableBody columns={4}>{t('inventory.nothingMissing')}</EmptyTableBody>
					) : (
						<TableBody>
							{rows.map((r) => (
								<TableRow key={r.trackingCode} data-testid={`${testId}-row`}>
									<TableCell className='font-mono'>{r.trackingCode}</TableCell>
									<TableCell>{r.recipientName || '—'}</TableCell>
									<TableCell className='text-right tabular-nums'>{r.amountOwed.toLocaleString('ru-RU')}</TableCell>
									<TableCell className='text-muted-foreground'>{r.paymentType ? t('inventory.paid') : ''}</TableCell>
								</TableRow>
							))}
						</TableBody>
					)}
				</Table>
			</div>
		</div>
	)
}
